import React, { useState, useCallback, useRef } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Upload, FileText, X, Loader2, CheckCircle2, File, 
  Image, FileSpreadsheet, Download, Trash2
} from 'lucide-react';

const FILE_ICONS = {
  pdf: { icon: FileText, color: 'text-red-500' },
  doc: { icon: FileText, color: 'text-blue-500' },
  docx: { icon: FileText, color: 'text-blue-500' },
  xls: { icon: FileSpreadsheet, color: 'text-green-500' },
  xlsx: { icon: FileSpreadsheet, color: 'text-green-500' },
  png: { icon: Image, color: 'text-cyan-500' },
  jpg: { icon: Image, color: 'text-cyan-500' },
  jpeg: { icon: Image, color: 'text-cyan-500' },
};

function getFileIcon(filename) {
  const ext = (filename || '').split('.').pop()?.toLowerCase() || '';
  const config = FILE_ICONS[ext] || { icon: File, color: 'text-slate-500' };
  const Icon = config.icon;
  return <Icon className={`h-5 w-5 ${config.color}`} />;
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileUploadZone({
  projectId,
  category = 'general',
  documentRecordId = null,
  onUploadComplete,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.msg,.eml,.dwg,.zip,.txt',
  maxFiles = 10,
  compact = false,
}) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const processFiles = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    const fileList = Array.from(files).slice(0, maxFiles);
    
    setUploading(true);
    setUploadProgress(fileList.map(f => ({ name: f.name, status: 'pending', size: f.size })));

    const results = [];
    for (let i = 0; i < fileList.length; i++) {
      setUploadProgress(prev => prev.map((p, idx) => 
        idx === i ? { ...p, status: 'uploading' } : p
      ));
      try {
        const res = await api.files.upload(projectId, fileList[i], category, documentRecordId);
        results.push(res.data);
        setUploadProgress(prev => prev.map((p, idx) =>
          idx === i ? { ...p, status: 'done' } : p
        ));
      } catch (err) {
        setUploadProgress(prev => prev.map((p, idx) =>
          idx === i ? { ...p, status: 'error', error: err.message } : p
        ));
      }
    }

    const successCount = results.length;
    if (successCount > 0) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
      onUploadComplete?.(results);
    }

    setTimeout(() => {
      setUploadProgress([]);
      setUploading(false);
    }, 2000);
  }, [projectId, category, documentRecordId, maxFiles, onUploadComplete]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const handleFileSelect = useCallback((e) => {
    processFiles(e.target.files);
    e.target.value = '';
  }, [processFiles]);

  if (compact) {
    return (
      <div data-testid="file-upload-zone-compact">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          accept={accept}
          className="hidden"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          data-testid="upload-file-btn"
        >
          {uploading ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading...</>
          ) : (
            <><Upload className="h-4 w-4 mr-1" /> Upload File</>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="file-upload-zone">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        multiple
        accept={accept}
        className="hidden"
      />
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-200 hover:border-slate-400 bg-slate-50 hover:bg-slate-100'
          }
          ${uploading ? 'pointer-events-none opacity-60' : ''}
        `}
        data-testid="file-drop-area"
      >
        {uploading ? (
          <Loader2 className="h-8 w-8 text-slate-400 mx-auto mb-2 animate-spin" />
        ) : (
          <Upload className={`h-8 w-8 mx-auto mb-2 ${dragActive ? 'text-blue-500' : 'text-slate-400'}`} />
        )}
        <p className="text-sm font-medium text-slate-600">
          {dragActive ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          PDF, Word, Excel, Images, DWG (max 50MB each)
        </p>
      </div>

      {uploadProgress.length > 0 && (
        <div className="mt-3 space-y-2">
          {uploadProgress.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-white border border-slate-200 rounded p-2">
              {getFileIcon(item.name)}
              <span className="text-sm text-slate-700 truncate flex-1">{item.name}</span>
              <span className="text-xs text-slate-400">{formatFileSize(item.size)}</span>
              {item.status === 'uploading' && <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />}
              {item.status === 'done' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
              {item.status === 'error' && <X className="h-4 w-4 text-red-500" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FileList({ files = [], onDownload, onDelete, showDelete = true }) {
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (file) => {
    setDownloading(file.id);
    try {
      if (onDownload) {
        await onDownload(file);
      } else {
        const response = await api.files.download(file.id);
        const blob = new Blob([response.data]);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.original_filename || 'download';
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (file) => {
    try {
      await api.files.delete(file.id);
      toast.success('File removed');
      onDelete?.(file);
    } catch {
      toast.error('Delete failed');
    }
  };

  if (!files.length) return null;

  return (
    <div className="space-y-1.5" data-testid="file-list">
      {files.map((file) => (
        <div
          key={file.id}
          className="flex items-center gap-2 bg-white border border-slate-200 rounded p-2 hover:bg-slate-50"
          data-testid={`file-item-${file.id}`}
        >
          {getFileIcon(file.original_filename)}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-700 truncate">{file.original_filename}</p>
            <p className="text-xs text-slate-400">{formatFileSize(file.size)}</p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => handleDownload(file)}
              disabled={downloading === file.id}
              data-testid={`download-file-${file.id}`}
            >
              {downloading === file.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5 text-slate-500" />
              )}
            </Button>
            {showDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 hover:text-red-500"
                onClick={() => handleDelete(file)}
                data-testid={`delete-file-${file.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
